import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Loader2, PhoneCall, MessageCircle, ExternalLink, CreditCard, StickyNote,
  CheckCircle2, ArrowLeft, Store, User, Mail, MapPin, Calendar, Timer, Users, Wallet,
  TrendingUp, AlertTriangle, Sparkles, ShieldCheck,
} from "lucide-react";
import { buildWaLink } from "../lib/usePlatformWa";
import { useAuth } from "../lib/auth";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-PK", { day: "numeric", month: "short", year: "numeric" }) : "—");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-PK") : "—");
const fmtPKR = (n) => `PKR ${Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;

function StatusPill({ status }) {
  const map = {
    trial:      "bg-amber-100 text-amber-800 border-amber-200",
    active:     "bg-emerald-100 text-emerald-800 border-emerald-200",
    subscribed: "bg-emerald-100 text-emerald-800 border-emerald-200",
    expired:    "bg-rose-100 text-rose-800 border-rose-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${map[status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {String(status || "unknown").toUpperCase()}
    </span>
  );
}

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
        <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">{title}</div>
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between items-start gap-3 py-1.5 border-b border-slate-100 last:border-0">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 text-right font-medium" data-testid={testid}>{value ?? "—"}</div>
    </div>
  );
}

export default function StaffShopDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const isAdmin = user?.role === "platform_admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/staff/shops/${encodeURIComponent(id)}`);
      setData(r.data);
    } catch (err) {
      const code = err?.response?.status;
      setError({
        code,
        message: err?.response?.data?.detail || "Shop details load nahi ho payi. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const addNote = async (kind = "note") => {
    if (!note.trim()) return toast.error("Note khali hai.");
    setSaving(true);
    try {
      await api.post(`/staff/shops/${encodeURIComponent(id)}/notes`, { note: note.trim(), kind });
      setNote("");
      toast.success("Note saved.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const markFollowed = async () => {
    setSaving(true);
    try {
      await api.post(`/staff/shops/${encodeURIComponent(id)}/mark-followed`);
      toast.success("Follow-up marked complete.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="grid place-items-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;

  if (error) {
    return (
      <div className="max-w-2xl mx-auto rounded-md border border-slate-200 bg-white p-8 text-center" data-testid="staff-shop-error">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
        <h2 className="font-display text-xl font-bold mt-3 text-slate-900">
          {error.code === 404 ? "Shop nahi mila" : error.code === 403 ? "Access denied" : "Load failed"}
        </h2>
        <p className="text-sm text-slate-600 mt-2">
          {error.code === 403
            ? "Yeh shop aap ko assign nahi kiya gaya. Sirf apne assigned shops dekh sakte hain."
            : error.code === 404
              ? "Yeh shop database mein maujood nahi hai. Shayed ID galat hai ya shop delete ho chuka hai."
              : error.message}
        </p>
        <div className="mt-4 flex justify-center gap-2">
          <Button variant="outline" onClick={() => nav("/staff/trial-followups")} data-testid="staff-shop-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Trial Follow-ups
          </Button>
        </div>
      </div>
    );
  }

  const { shop, subscription, referral, verification, stats, timeline } = data;
  const waLink = shop.owner_mobile ? buildWaLink(shop.owner_mobile, `Assalam-o-Alaikum ${shop.owner_name || ""}, HisaabPro trial expiry ke bare mein baat karni thi.`) : "";

  return (
    <div className="space-y-4" data-testid="staff-shop-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
            <Link to="/staff/trial-followups" className="hover:text-slate-800 flex items-center gap-1" data-testid="staff-shop-breadcrumb">
              <ArrowLeft className="w-3.5 h-3.5" /> Trial Follow-ups
            </Link>
          </div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-3 flex-wrap">
            <Store className="w-7 h-7 text-emerald-600" />
            {shop.name}
            <StatusPill status={shop.status} />
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            {shop.owner_name || "Owner"} · {shop.city || "—"} ·{" "}
            {shop.days_delta === null ? "" :
             shop.days_delta < 0 ? <b className="text-rose-700">{-shop.days_delta} day(s) expired</b> :
             shop.days_delta === 0 ? <b className="text-amber-700">Expires today</b> :
             <b className="text-slate-700">{shop.days_delta} day(s) remaining</b>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {shop.owner_mobile && (
            <>
              <a href={`tel:${shop.owner_mobile}`} className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1.5" data-testid="staff-shop-call-btn">
                <PhoneCall className="w-4 h-4" /> Call
              </a>
              <a href={waLink} target="_blank" rel="noreferrer" className="text-sm px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1.5" data-testid="staff-shop-whatsapp-btn">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </>
          )}
          <a href={`/admin/shops?open=${encodeURIComponent(shop.id)}`} className={`text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1.5 ${isAdmin ? "" : "hidden"}`} data-testid="staff-shop-full-dashboard-btn">
            <ExternalLink className="w-4 h-4" /> Full Dashboard
          </a>
          <a href={`/admin/shops?open=${encodeURIComponent(shop.id)}&upgrade=1`} className={`text-sm px-3 py-1.5 rounded-md bg-sky-600 hover:bg-sky-700 text-white inline-flex items-center gap-1.5 ${isAdmin ? "" : "hidden"}`} data-testid="staff-shop-upgrade-btn">
            <CreditCard className="w-4 h-4" /> Upgrade
          </a>
          <button type="button" onClick={markFollowed} disabled={saving} className="text-sm px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1.5" data-testid="staff-shop-mark-followed-btn">
            <CheckCircle2 className="w-4 h-4" /> Mark followed
          </button>
        </div>
      </div>

      {/* Customer stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="staff-shop-stats-grid">
        <StatMini label="Total Customers"    value={stats.total_customers}          icon={Users}         tone="slate"   testid="stat-customers" />
        <StatMini label="Active Loans"       value={stats.active_loans}             icon={CreditCard}    tone="sky"     testid="stat-active-loans" />
        <StatMini label="Total Outstanding"  value={fmtPKR(stats.total_outstanding)} icon={Wallet}       tone="amber"   testid="stat-outstanding" />
        <StatMini label="Today's Collection" value={fmtPKR(stats.today_collection)} icon={TrendingUp}    tone="emerald" testid="stat-today-collection" />
        <StatMini label="Overdue Customers"  value={stats.overdue_customers}        icon={AlertTriangle} tone="rose"    testid="stat-overdue" />
        <StatMini label="Recovery Rate"      value={`${stats.recovery_rate}%`}      icon={ShieldCheck}   tone="emerald" testid="stat-recovery" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Shop Info */}
        <Card title="Shop Information" icon={Store} testid="card-shop-info">
          <Row label="Shop Name"        value={shop.name} testid="det-shop-name" />
          <Row label="Owner Name"       value={shop.owner_name} testid="det-owner-name" />
          <Row label="Mobile"           value={shop.owner_mobile} testid="det-mobile" />
          <Row label="Email"            value={shop.owner_email} testid="det-email" />
          <Row label="City"             value={shop.city} testid="det-city" />
          <Row label="Address"          value={shop.address} testid="det-address" />
          <Row label="Registered On"    value={fmtDate(shop.created_at)} testid="det-registered" />
          <Row label="Trial Start"      value={fmtDate(shop.verified_at || shop.created_at)} testid="det-trial-start" />
          <Row label="Trial Ends"       value={fmtDate(shop.trial_ends)} testid="det-trial-ends" />
          <Row label="Current Status"   value={<StatusPill status={shop.status} />} />
        </Card>

        {/* Subscription */}
        <Card title="Subscription" icon={CreditCard} testid="card-subscription">
          <Row label="Current Plan"    value={<span className="uppercase">{subscription.current_plan}</span>} testid="sub-plan" />
          <Row label="Trial Days"      value={`${subscription.trial_days_configured} days`} />
          <Row label="Status"          value={<StatusPill status={subscription.status} />} />
          <Row label="Last Payment"    value={subscription.last_payment ? `${fmtDate(subscription.last_payment.created_at)} · ${fmtPKR(subscription.last_payment.amount)}` : "—"} testid="sub-last-payment" />
          <Row label="Next Renewal"    value={fmtDate(subscription.next_renewal)} />
          <Row label="Outstanding"     value={fmtPKR(subscription.outstanding)} />
        </Card>

        {/* Referral */}
        {referral ? (
          <Card title="Referral" icon={Sparkles} testid="card-referral">
            <Row label="Referrer Shop"   value={referral.referrer_name} testid="ref-shop-name" />
            <Row label="Referral Code"   value={<span className="font-mono">{referral.referral_code}</span>} testid="ref-code" />
            <Row label="Referral Date"   value={fmtDate(referral.referral_date)} />
            <Row label="Commission Status" value={referral.commission_status || "—"} testid="ref-comm-status" />
            {referral.commission_amount != null && (
              <Row label="Commission" value={`${fmtPKR(referral.commission_amount)} @ ${referral.commission_rate}% (${referral.commission_type || "first"})`} />
            )}
          </Card>
        ) : (
          <Card title="Referral" icon={Sparkles} testid="card-referral">
            <div className="text-xs text-slate-500 py-2">This shop was not referred by anyone.</div>
          </Card>
        )}

        {/* Verification */}
        <Card title="Verification" icon={ShieldCheck} testid="card-verification">
          <Row label="Status"           value={verification.status || "—"} />
          <Row label="Assigned Staff"   value={verification.assigned_staff ? `${verification.assigned_staff.full_name || verification.assigned_staff.email} (${verification.assigned_staff.role})` : "—"} testid="det-assigned-staff" />
          <Row label="Verified On"      value={fmtDate(verification.verified_at)} />
          <Row label="Last Follow-up"   value={fmtDateTime(verification.last_followup)} testid="det-last-followup" />
          {verification.notes && <Row label="Notes" value={<span className="text-xs whitespace-pre-wrap">{verification.notes}</span>} />}
        </Card>
      </div>

      {/* Add Note + Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-1">
          <Card title="Add Follow-up Note" icon={StickyNote} testid="card-add-note">
            <textarea
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Kya baat hui? Kya next step hai?"
              className="w-full text-sm rounded-md border border-slate-300 p-2 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              data-testid="staff-shop-note-input"
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              <button onClick={() => addNote("note")}     disabled={saving} className="text-xs px-2.5 py-1 rounded border border-slate-300 hover:bg-slate-100" data-testid="staff-shop-note-save-btn">Save Note</button>
              <button onClick={() => addNote("call")}     disabled={saving} className="text-xs px-2.5 py-1 rounded bg-sky-100 border border-sky-300 text-sky-800" data-testid="staff-shop-note-call-btn">Log Call</button>
              <button onClick={() => addNote("whatsapp")} disabled={saving} className="text-xs px-2.5 py-1 rounded bg-emerald-100 border border-emerald-300 text-emerald-800" data-testid="staff-shop-note-wa-btn">Log WhatsApp</button>
            </div>
          </Card>
        </div>
        <div className="lg:col-span-2">
          <Card title={`Timeline (${(timeline || []).length})`} icon={Calendar} testid="card-timeline">
            {(!timeline || timeline.length === 0) ? (
              <div className="text-xs text-slate-500 py-2">No activity yet.</div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {timeline.map((t, i) => (
                  <div key={i} className="py-2 flex items-start gap-2" data-testid={`staff-shop-timeline-row-${i}`}>
                    <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${
                      t.kind === "milestone" ? "bg-emerald-500" :
                      t.kind === "notification" ? "bg-amber-500" :
                      t.kind === "call" ? "bg-sky-500" :
                      t.kind === "whatsapp" ? "bg-emerald-600" : "bg-slate-400"
                    }`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-900 leading-snug">{t.event}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {fmtDateTime(t.at)}{t.author_email ? ` · by ${t.author_email}` : ""}{t.audience ? ` · ${t.audience}` : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function StatMini({ label, value, icon: Icon, tone, testid }) {
  const map = {
    slate:   "border-slate-200 bg-slate-50 text-slate-800",
    sky:     "border-sky-200 bg-sky-50 text-sky-800",
    amber:   "border-amber-200 bg-amber-50 text-amber-800",
    rose:    "border-rose-200 bg-rose-50 text-rose-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
  };
  return (
    <div className={`rounded-md border p-2.5 ${map[tone] || map.slate}`} data-testid={testid}>
      <div className="flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /><div className="text-[10px] uppercase tracking-widest font-bold">{label}</div></div>
      <div className="font-display text-lg font-bold mt-0.5 text-slate-900">{value}</div>
    </div>
  );
}
